import {
    buildTyphoonAreaIntensitySummary,
    buildTyphoonMovementSummary,
    buildTyphoonWindSummary,
    escapeHTML,
    formatMultilineTyphoonText,
    formatTyphoonDateTime,
    formatTyphoonPressure,
    formatTyphoonRadius,
    formatTyphoonWindSpeed,
    getTyphoonDisplayTitle
} from '../helpers/helpers-typhoon';
import Panel from './panel';

const SELECT_ALL = '__all__';

function buildHeaderHtml(isAllSelected = false) {
    return [
        '<div class="typhoon-panel-header">',
        '<span class="typhoon-panel-title-text">台風解析・予報情報</span>',
        `<span class="typhoon-panel-caption">${isAllSelected ? '地図には表示対象の台風をすべて表示しています。' : '地図には選択中の台風1件のみ表示しています。'}</span>`,
        '</div>'
    ].join('');
}

function buildLegendRow(sampleClassName, label) {
    return [
        '<div class="typhoon-legend-row">',
        `<span class="typhoon-legend-swatch ${sampleClassName}"></span>`,
        `<span class="typhoon-legend-label">${escapeHTML(label)}</span>`,
        '</div>'
    ].join('');
}

function buildTyphoonLegendHtml() {
    return [
        '<div class="typhoon-legend-title">台風図の凡例</div>',
        '<div class="typhoon-legend-list">',
        buildLegendRow('typhoon-legend-swatch-strong-wind', '強風域'),
        buildLegendRow('typhoon-legend-swatch-storm-area', '暴風域'),
        buildLegendRow('typhoon-legend-swatch-storm-warning', '暴風警戒域'),
        buildLegendRow('typhoon-legend-swatch-probability-circle', '予報円'),
        '</div>'
    ].join('');
}

function buildSummaryRow(label, value, className = '') {
    return [
        `<div class="typhoon-summary-row ${className}">`,
        `<div class="typhoon-summary-label">${escapeHTML(label)}</div>`,
        `<div class="typhoon-summary-value">${value || '-'}</div>`,
        '</div>'
    ].join('');
}

function buildForecastRows(points) {
    if (!points.length) {
        return '<div class="typhoon-empty">表示できる予報データがありません。</div>';
    }

    return points.map(point => [
        '<div class="typhoon-forecast-row">',
        '<div class="typhoon-forecast-heading">',
        `<span class="typhoon-forecast-label">${escapeHTML(point.label || '予報')}</span>`,
        `<span class="typhoon-forecast-time">${escapeHTML(formatTyphoonDateTime(point.forecast_time, false) || '-')}</span>`,
        '</div>',
        `<div class="typhoon-forecast-location">${escapeHTML(point.center_location || '位置情報なし')}</div>`,
        '<div class="typhoon-forecast-meta">',
        `中心気圧 ${escapeHTML(formatTyphoonPressure(point.center_pressure))} / `,
        `最大風速 ${escapeHTML(formatTyphoonWindSpeed(point.max_wind_speed_ms))} / `,
        `予報円 ${escapeHTML(formatTyphoonRadius(point.probability_circle_radius_km))}`,
        '</div>',
        '</div>'
    ].join('')).join('');
}

function buildTyphoonEventRows(typhoons) {
    if (!typhoons.length) {
        return '<div class="typhoon-empty">台風解析・予報情報はありません。</div>';
    }

    return typhoons.map(typhoon => {
        const current = typhoon.current || {};

        return [
            `<div class="typhoon-event-row" data-eventid="${escapeHTML(typhoon.eventid)}" role="button" tabindex="0">`,
            '<div class="typhoon-forecast-heading">',
            `<span class="typhoon-forecast-label">${escapeHTML(typhoon.displayTitle || getTyphoonDisplayTitle(typhoon))}</span>`,
            `<span class="typhoon-forecast-time">${escapeHTML(formatTyphoonDateTime(typhoon.report_datetime, false) || '-')}</span>`,
            '</div>',
            `<div class="typhoon-forecast-location">${escapeHTML(current.center_location || '位置情報なし')}</div>`,
            `<div class="typhoon-forecast-meta">${escapeHTML(buildTyphoonAreaIntensitySummary(typhoon))} / ${escapeHTML(buildTyphoonWindSummary(typhoon))}</div>`,
            '</div>'
        ].join('');
    }).join('');
}

function buildDetailSection(detail = {}) {
    const comment = String(detail.comment || '').trim();

    if (!comment) {
        return '';
    }

    return [
        '<div class="typhoon-section-title">詳細</div>',
        `<div class="typhoon-text-block">${formatMultilineTyphoonText(comment)}</div>`
    ].join('');
}

function buildTyphoonPanelHtml(typhoon) {
    const current = typhoon.current || {},
        forecastPoints = [current, ...(typhoon.forecasts || [])].filter(Boolean),
        detailSection = buildDetailSection(typhoon.detail);

    return [
        '<div class="typhoon-panel-body">',
        '<div class="typhoon-event-switcher">',
        '<select class="typhoon-event-select" aria-label="台風解析・予報情報の選択"></select>',
        '</div>',
        '<div class="typhoon-summary-grid">',
        buildSummaryRow('発表時刻', escapeHTML(formatTyphoonDateTime(typhoon.report_datetime) || '-')),
        buildSummaryRow('現在位置', escapeHTML(current.center_location || '-')),
        buildSummaryRow('大きさ / 強さ', escapeHTML(buildTyphoonAreaIntensitySummary(typhoon))),
        buildSummaryRow('方向・速さ', escapeHTML(buildTyphoonMovementSummary(typhoon))),
        buildSummaryRow('中心気圧', escapeHTML(formatTyphoonPressure(current.center_pressure))),
        buildSummaryRow('最大風速 / 最大瞬間風速', escapeHTML(buildTyphoonWindSummary(typhoon)), 'typhoon-summary-row-highlight'),
        '</div>',
        '<div class="typhoon-section-title">予報一覧</div>',
        `<div class="typhoon-forecast-list">${buildForecastRows(forecastPoints)}</div>`,
        detailSection,
        '</div>'
    ].join('');
}

function buildAllTyphoonPanelHtml(typhoons) {
    const latestTyphoon = typhoons[0] || null,
        strongestTyphoon = typhoons.reduce((bestTyphoon, typhoon) => {
            if (!bestTyphoon) {
                return typhoon;
            }

            const bestWind = Number(bestTyphoon.current?.max_wind_speed_ms || 0),
                currentWind = Number(typhoon.current?.max_wind_speed_ms || 0);

            return currentWind > bestWind ? typhoon : bestTyphoon;
        }, null);

    return [
        '<div class="typhoon-panel-body">',
        '<div class="typhoon-event-switcher">',
        '<select class="typhoon-event-select" aria-label="台風解析・予報情報の選択"></select>',
        '</div>',
        '<div class="typhoon-summary-grid">',
        buildSummaryRow('件数', escapeHTML(`${typhoons.length}件`)),
        buildSummaryRow('最新発表時刻', escapeHTML(formatTyphoonDateTime(latestTyphoon?.report_datetime) || '-')),
        buildSummaryRow(
            '表示対象',
            escapeHTML(typhoons.map(typhoon => typhoon.displayTitle || getTyphoonDisplayTitle(typhoon)).join(' / ') || '-')
        ),
        buildSummaryRow(
            '最大風速 / 最大瞬間風速',
            escapeHTML(strongestTyphoon ? buildTyphoonWindSummary(strongestTyphoon) : '-'),
            'typhoon-summary-row-highlight'
        ),
        '</div>',
        '<div class="typhoon-section-title">台風一覧</div>',
        `<div class="typhoon-forecast-list">${buildTyphoonEventRows(typhoons)}</div>`,
        '</div>'
    ].join('');
}

function buildTyphoonOptionLabel(typhoon) {
    return [
        formatTyphoonDateTime(typhoon.report_datetime || '', false) || '時刻不明',
        typhoon.displayTitle || getTyphoonDisplayTitle(typhoon)
    ].filter(Boolean).join(' ');
}

function bindSelectableRows(container, selector, onSelect) {
    const rows = container ? Array.from(container.querySelectorAll(selector)) : [];

    rows.forEach(row => {
        const triggerSelection = () => {
            const eventId = row.dataset.eventid;

            if (eventId) {
                onSelect(eventId);
            }
        };

        row.addEventListener('click', event => {
            event.stopPropagation();
            triggerSelection();
        });
        row.addEventListener('keydown', event => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                event.stopPropagation();
                triggerSelection();
            }
        });
    });
}

export default class extends Panel {

    constructor(options) {
        super(Object.assign({
            className: 'typhoon-panel',
            typhoons: [],
            selectedEventId: '',
            headerClickAction: 'close'
        }, options));
    }

    addTo(map) {
        const me = this;

        me._closing = false;
        super.addTo(map)
            .setTitle(buildHeaderHtml())
            .updateContent();
        me.renderLegend();

        me._container.querySelector('#panel-button').addEventListener('click', event => {
            event.stopPropagation();
            me.remove();
        });

        return me;
    }

    setTyphoons(typhoons, selectedEventId = this._options.selectedEventId) {
        this._options.typhoons = Array.isArray(typhoons) ? typhoons : [];
        this._options.selectedEventId = selectedEventId || '';

        if (this.isOpen()) {
            this.updateContent();
        }
        return this;
    }

    setSelectedEventId(selectedEventId) {
        this._options.selectedEventId = selectedEventId || '';

        if (this.isOpen()) {
            this.updateContent();
        }
        return this;
    }

    isAllSelected() {
        return this._options.selectedEventId === SELECT_ALL;
    }

    getSelectedTyphoon() {
        const typhoons = this._options.typhoons || [],
            selectedEventId = this._options.selectedEventId;

        if (selectedEventId === SELECT_ALL) {
            return null;
        }
        return typhoons.find(typhoon => typhoon.eventid === selectedEventId) || typhoons[0] || null;
    }

    updateEventOptions() {
        const me = this,
            select = me._eventSelect,
            typhoons = me._options.typhoons || [],
            selectedTyphoon = me.getSelectedTyphoon(),
            selectedValue = me.isAllSelected() ?
                SELECT_ALL :
                (selectedTyphoon ? selectedTyphoon.eventid : typhoons[0]?.eventid || '');

        if (!select) {
            return me;
        }

        select.innerHTML = '';

        if (!typhoons.length) {
            const option = document.createElement('option');

            option.value = '';
            option.textContent = '台風解析・予報情報はありません';
            select.appendChild(option);
            select.disabled = true;
            return me;
        }

        const allOption = document.createElement('option');

        allOption.value = SELECT_ALL;
        allOption.textContent = 'すべて';
        select.appendChild(allOption);

        typhoons.forEach(typhoon => {
            const option = document.createElement('option');

            option.value = typhoon.eventid;
            option.textContent = buildTyphoonOptionLabel(typhoon);
            select.appendChild(option);
        });
        select.disabled = false;
        select.value = selectedValue;

        return me;
    }

    bindEventSelect() {
        const me = this,
            select = me._container && me._container.querySelector('.typhoon-event-select');

        if (!select) {
            me._eventSelect = null;
            return me;
        }
        if (select === me._eventSelect) {
            return me;
        }

        me._eventSelect = select;
        ['mousedown', 'click', 'touchstart'].forEach(eventName => {
            select.addEventListener(eventName, event => {
                event.stopPropagation();
            });
        });
        select.addEventListener('change', event => {
            const onSelectTyphoon = me._options.onSelectTyphoon;

            me.setSelectedEventId(event.target.value);
            if (onSelectTyphoon) {
                onSelectTyphoon(event.target.value);
            }
        });

        return me;
    }

    bindEventRows() {
        const me = this,
            onSelectTyphoon = me._options.onSelectTyphoon;

        if (!onSelectTyphoon || !me._container) {
            return me;
        }

        bindSelectableRows(me._container, '.typhoon-event-row[data-eventid]', eventId => {
            me.setSelectedEventId(eventId);
            onSelectTyphoon(eventId);
        });

        return me;
    }

    renderLegend() {
        const me = this;

        if (!me._container) {
            return me;
        }
        if (!me._options.typhoons?.length) {
            return me.removeLegend();
        }
        if (!me._legendElement) {
            me._legendElement = document.createElement('div');
            me._legendElement.className = 'typhoon-legend';
            me._container.appendChild(me._legendElement);
        }

        me._legendElement.innerHTML = buildTyphoonLegendHtml();
        return me;
    }

    removeLegend() {
        const me = this,
            legendElement = me._legendElement;

        if (legendElement && legendElement.parentNode) {
            legendElement.parentNode.removeChild(legendElement);
        }
        me._legendElement = null;
        return me;
    }

    updateContent() {
        const typhoons = this._options.typhoons || [],
            typhoon = this.getSelectedTyphoon(),
            isAllSelected = this.isAllSelected();

        this.setTitle(buildHeaderHtml(isAllSelected));

        if (!typhoons.length) {
            this.setHTML('<div class="typhoon-empty">台風解析・予報情報はありません。</div>');
            this._eventSelect = null;
            this.removeLegend();
            return this;
        }

        this.setHTML(
            isAllSelected ?
                buildAllTyphoonPanelHtml(typhoons) :
                buildTyphoonPanelHtml(typhoon)
        );
        this.bindEventSelect();
        this.updateEventOptions();
        this.bindEventRows();
        this.renderLegend();
        return this;
    }

    isClosing() {
        return !!this._closing;
    }

    remove() {
        const me = this,
            onClose = me._options.onClose;

        if (!me.isOpen()) {
            return me;
        }

        me._closing = true;
        me.removeLegend();
        super.remove();

        if (onClose) {
            onClose();
        }
        setTimeout(() => {
            me._closing = false;
        }, 300);
        return me;
    }

}
